/**
 * KB-3718 — the ONE reader of where a pipeline price was stated, for every review surface.
 *
 * KB-3698 stamps `observedPriceLevel: "session" | "programme" | null` (+ `observedProgrammePrice`, and
 * `observedPriceRefusal` when a present programme price was not used) onto every writer audit row via one
 * projection, `priceProvenanceFields` (`observed-to-offers.cjs`). A programme-level price is one the vendor
 * stated once for the whole programme; a reviewer must be able to tell it from a price the session stated
 * itself, or they cannot audit it.
 *
 * FOUR states, not three, and the fourth is the point:
 *   - `session`   — the session stated its own price.
 *   - `programme` — the programme stated it once; carries the page and quote so it can be checked.
 *   - `none`      — the writer recorded that NO price was stated (`observedPriceLevel: null`).
 *   - `unknown`   — we could not find out. Never collapsed into any of the other three.
 *
 * `none` vs `unknown` hinges on the KEY being present: `priceProvenanceFields` always writes
 * `observedPriceLevel` (null when nothing was stated), so a row WITHOUT the key predates KB-3698 or came
 * through a projection that dropped it. Reading a missing key as `session` is the reassuring wrong answer
 * this ticket exists to prevent; reading it as `none` would claim a fact nobody recorded.
 *
 * PURE — no imports. `campDataReviewInbox.ts` reads it, so it must not pull that module's graph back in;
 * the IO join for creations lives in `campDataPriceLevelJoin.ts`.
 */

/** Mirrors `OBSERVED_PRICE_LEVELS` in `scripts/camp-data/_shared/priceProvenanceLib.cjs`. */
export const PRICE_LEVEL_SESSION = "session";
export const PRICE_LEVEL_PROGRAMME = "programme";

/**
 * Why a level is unknown. A runtime list (not only a type) so the census lib's `UNKNOWN_REASONS` can be pinned
 * to it by `campDataPriceLevel.parity.test.ts`.
 */
export const PRICE_LEVEL_UNKNOWN_REASONS = [
  // The page has not finished looking yet — shown while the audit join runs, never left in place.
  "pending",
  // The row carries no `observedPriceLevel` key: written before KB-3698, or by a projection that dropped it.
  "not-recorded",
  // The key holds a value this build does not know.
  "unrecognized",
  // The offer has no pipeline `sourceKey`, so there is no run to join it to (a human or agent create).
  "no-source-key",
  // No run of this camp started at or before the offer was created.
  "no-run",
  // The runs could not be read.
  "runs-unreadable",
  // The run's writer audit could not be fetched (missing or failed).
  "audit-unavailable",
  // The audit was read but does not list this offer.
  "not-in-audit",
  // The lookup itself failed unexpectedly (a throw, not a failed read) — a reload may fix it.
  "lookup-failed",
  // The change names no offer, so there is nothing to join.
  "no-offer",
  // The page stopped showing "checking…" at its deadline with the join still running (KB-3835). A later answer
  // still replaces it.
  "timed-out",
] as const;

export type PriceLevelUnknownReason = (typeof PRICE_LEVEL_UNKNOWN_REASONS)[number];

export interface ProgrammePrice {
  priceText: string | null;
  sourceUrl: string | null;
  quote: string | null;
}

/**
 * How a JOINED level found its audit row (dispatcher, KB-3718 comment 24ab3321). An inferred run reading as an
 * exact one is the same calm wrong answer as unknown reading as session, so a joined level always names it:
 *   - `exact-run`    — the change record names its run (`campSlug` + `runId` on a contribution row).
 *   - `inferred-run` — the offer names no run; the run was picked by timing (`rankRunsForCreation`).
 * Absent = no join: the surface read the run's own audit row (Inbox, Run details, Adopt, confirm dialog).
 */
export type PriceLevelSource = "exact-run" | "inferred-run";

export type PriceLevel = (
  | { state: "session" }
  | { state: "programme"; programmePrice: ProgrammePrice | null }
  | { state: "none"; refusalCode: string | null }
  // KB-3835 — the session stated no price and was created at its program's default (`offers.priceSource:
  // "program-default"`, KB-3769). Read off the offer, never an audit row, so `readPriceLevel` never returns it.
  | { state: "program-default" }
  | { state: "unknown"; reason: PriceLevelUnknownReason }
) & { source?: PriceLevelSource };

export function strOrNull(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

/** The refusal's code when `v` is a refusal object carrying one — the writer's `{ code, reason }` shape. */
function refusalCodeOf(v: unknown): string | null {
  return v && typeof v === "object" && !Array.isArray(v) ? strOrNull((v as { code?: unknown }).code) : null;
}

function readProgrammePrice(v: unknown): ProgrammePrice | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const out = { priceText: strOrNull(o.priceText), sourceUrl: strOrNull(o.sourceUrl), quote: strOrNull(o.quote) };
  return out.priceText || out.sourceUrl || out.quote ? out : null;
}

/**
 * The level carried by the FIRST row that has the `observedPriceLevel` key (own property). Pass the row and
 * its enrichment in precedence order — the same order the surface reads every other field in.
 */
export function readPriceLevel(...rows: Array<Record<string, unknown> | null | undefined>): PriceLevel {
  for (const r of rows) {
    if (!r || typeof r !== "object" || !Object.prototype.hasOwnProperty.call(r, "observedPriceLevel")) continue;
    const level = r.observedPriceLevel;
    // A REFUSAL overrides the level (code-review round 2, finding 1). `resolveObservedPrice`
    // (observedToOffersLib.cjs) records a refused programme price as level "programme" — or "session" for
    // PROGRAMME_PRICE_SHADOWED_BY_SESSION — and carries NO price. Reading the level alone would name a price
    // that was refused as the one that was used. No usable price was stated: that is "none", with its code.
    const refusalCode = refusalCodeOf(r.observedPriceRefusal);
    if (refusalCode) return { state: "none", refusalCode };
    if (level === PRICE_LEVEL_SESSION) return { state: "session" };
    if (level === PRICE_LEVEL_PROGRAMME) return { state: "programme", programmePrice: readProgrammePrice(r.observedProgrammePrice) };
    if (level === null) return { state: "none", refusalCode: null };
    return { state: "unknown", reason: "unrecognized" };
  }
  return { state: "unknown", reason: "not-recorded" };
}

const UNKNOWN_REASON_TEXT: Record<PriceLevelUnknownReason, string> = {
  pending: "still checking the run's audit",
  "not-recorded": "not recorded on this row",
  unrecognized: "unrecognized value",
  "no-source-key": "not a pipeline-created session",
  "no-run": "no run found for this change",
  "runs-unreadable": "could not read the camp's runs",
  "audit-unavailable": "could not read the run's audit",
  "not-in-audit": "the run's audit does not list this session",
  "lookup-failed": "the lookup failed — reload to retry",
  "no-offer": "this change names no offer",
  "timed-out": "the lookup is taking too long — reload to retry",
};

export interface PriceLevelDescription {
  /** Short chip text. Every state has its own, so no two states can render the same. */
  text: string;
  /** Hover detail. */
  title: string;
  /**
   * Why, rendered on the chip itself: an `unknown`'s reason (a reviewer must see the join failed, not hover
   * for it), or a refused price's code.
   */
  reasonText?: string;
  /** Which join found the row, rendered on the chip — absent when the surface read the run's own audit. */
  sourceText?: string;
}

const SOURCE_TEXT: Record<PriceLevelSource, string> = {
  "exact-run": "run named by the change",
  "inferred-run": "run inferred from timing",
};

export function describePriceLevel(level: PriceLevel): PriceLevelDescription {
  const d = describeState(level);
  if (!level.source || (level.state === "unknown" && level.reason === "pending")) return d;
  const sourceText = SOURCE_TEXT[level.source];
  return { ...d, title: `${d.title} Audit row found via: ${sourceText}.`, sourceText };
}

function describeState(level: PriceLevel): PriceLevelDescription {
  switch (level.state) {
    case "session":
      return { text: "price: session-stated", title: "The vendor stated this price on the session itself." };
    case "programme": {
      const p = level.programmePrice;
      const quote = p?.quote ? ` Quote: “${p.quote}”.` : "";
      const page = p?.sourceUrl ? ` Page: ${p.sourceUrl}.` : " The programme page was not recorded.";
      return {
        text: "price: programme-level",
        title: `The vendor stated one price for the whole programme; the session did not state its own.${page}${quote}`,
      };
    }
    case "none":
      // A refusal is not "nothing was stated": a price WAS stated and the writer refused to use it, so the
      // chip names that, with the code, rather than reading like a session that simply said nothing.
      return level.refusalCode
        ? {
            text: "price: stated but refused",
            title: `A price was stated but the writer refused to use it (${level.refusalCode}), so no stated price backs this row.`,
            reasonText: level.refusalCode,
          }
        : { text: "price: none stated", title: "No price was stated for this session." };
    case "program-default":
      return {
        text: "price: program default",
        title: "The session stated no price of its own, so it was created at its program's default price.",
      };
    case "unknown":
      if (level.reason === "pending") {
        return { text: "price level: checking…", title: "Looking up where this price was stated in the run's audit." };
      }
      return {
        text: "price level unknown",
        title: `Where this price was stated could not be determined: ${UNKNOWN_REASON_TEXT[level.reason]}. Do not assume it was stated on the session.`,
        reasonText: UNKNOWN_REASON_TEXT[level.reason],
      };
  }
}

/**
 * The level for a persisted member entry (Run-details drill-downs) — or null when the entry shows no price
 * and records no level, so an unrelated row gains no chip. A price on screen always gets one.
 */
export function priceLevelForEntry(entry: object | null | undefined, priceShown: boolean): PriceLevel | null {
  const r = (entry ?? null) as Record<string, unknown> | null;
  return priceShown || recordsPriceLevel(r) ? readPriceLevel(r) : null;
}

/** Does this row carry the `observedPriceLevel` KEY (whatever its value, `null` included)? */
export function recordsPriceLevel(row: object | null | undefined): boolean {
  return !!row && typeof row === "object" && Object.prototype.hasOwnProperty.call(row, "observedPriceLevel");
}
